import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box, Table, TableBody, TableCell, TableHead, TableRow,
  Typography, Chip, Paper, Container, Stack
} from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import LogoImage from '../assets/images/logo.png';
import { useNavigate } from 'react-router-dom';

// === DECODIFICAR JWT ===
const decodeToken = (token) => {
  if (!token) return null;
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64).split('').map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error('Error decodificando token:', e);
    return null;
  }
};

// === FORMATO HORA ===
const horaFormateada = (isoString) => {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', hour12: true });
  } catch (e) { return ''; }
};

const ESTADO_COLORES = {
  llamado: { bg: '#1B5E20', shadow: 'rgba(76, 175, 80, 0.6)' },
  atendido: { bg: '#FFA000', shadow: 'rgba(255, 152, 0, 0.6)' },
};

const PantallaCola = () => {
  const [data, setData] = useState({ cola: [], llamados: {} });
  const [wsStatus, setWsStatus] = useState('iniciando...');
  const ws = useRef(null);
  const audioReadyRef = useRef(false);
  const llamadosActualesRef = useRef({});
  const reconnectTimeout = useRef(null);

  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const payload = useMemo(() => decodeToken(token), [token]);
  const sucursalId = payload?.id_sucursal ?? payload?.sucursales?.[0];
  const nombreSucursal = payload?.nombre_sucursal || `Sucursal ${sucursalId || ''}`;
  const nombreVeterinaria = payload?.nombre_veterinaria || 'Pet´s Center';

  useEffect(() => {
    if (!token || !sucursalId) {
      navigate('/login');
    }
  }, [token, sucursalId, navigate]);

  useEffect(() => {
    if (!sucursalId || !token) return;

    //const wsUrl = `ws://192.168.10.12:8000/pacientes/ws/cola/${sucursalId}?token=${encodeURIComponent(token)}`;
    const wsUrl = `wss://galaxiadogplace.com/api/pacientes/ws/cola/${sucursalId}?token=${encodeURIComponent(token)}`;
    const socket = new WebSocket(wsUrl);
    ws.current = socket;

    socket.onopen = () => setWsStatus('conectado');

    socket.onmessage = (event) => {
      try {
        const mensaje = JSON.parse(event.data);
        if (mensaje.cola !== undefined && mensaje.llamados !== undefined) {
          const llamadosAnteriores = llamadosActualesRef.current;
          setData(mensaje);

          Object.entries(mensaje.llamados || {}).forEach(([consultorio, info]) => {
            if (info.estado === 'llamado') {
              const estabaLlamadoAntes = llamadosAnteriores[consultorio]?.estado === 'llamado';
              if (!estabaLlamadoAntes) {
                const texto = `Turno ${info.numero_turno}, ${info.nombre_paciente}, acérquese al consultorio ${consultorio.replace('consultorio_', '')}`;
                if (audioReadyRef.current) {
                  const utterance = new SpeechSynthesisUtterance(texto);
                  utterance.lang = 'es-ES';
                  window.speechSynthesis.speak(utterance);
                }
              }
            }
          });
        } else if (mensaje.tipo === 'repetir_llamado') {
          const texto = `Turno ${mensaje.numero_turno}, ${mensaje.nombre_paciente}, acérquese al consultorio ${mensaje.consultorio_id?.replace('consultorio_', '') || '?'}`;
          if (audioReadyRef.current) {
            const utterance = new SpeechSynthesisUtterance(texto);
            utterance.lang = 'es-ES';
            window.speechSynthesis.speak(utterance);
          }
        }
      } catch (e) { console.error('Error WS:', e); }
    };

    socket.onclose = (event) => {
      setWsStatus(`desconectado (${event.code})`);
      ws.current = null;
      if (!reconnectTimeout.current) {
        reconnectTimeout.current = setTimeout(() => {
          reconnectTimeout.current = null;
        }, 3000);
      }
    };

    return () => {
      if (ws.current) ws.current.close();
      if (reconnectTimeout.current) clearTimeout(reconnectTimeout.current);
    };
  }, [sucursalId, token]);

  useEffect(() => {
    const initVoice = () => { if (window.speechSynthesis.getVoices().length > 0) audioReadyRef.current = true; };
    initVoice();
    window.speechSynthesis.onvoiceschanged = initVoice;
  }, []);

  useEffect(() => { llamadosActualesRef.current = data.llamados || {}; }, [data.llamados]);

  // === TARJETAS DE LLAMADOS ===
  const tarjetas = Object.entries(data.llamados || {}).map(([id, info]) => (
    <Paper
      key={id}
      elevation={20}
      sx={{
        width: 320, height: 240, borderRadius: 4, p: 3, textAlign: 'center', color: 'white',
        bgcolor: ESTADO_COLORES[info.estado]?.bg || '#1565C0',
        boxShadow: `0 0 30px ${ESTADO_COLORES[info.estado]?.shadow || 'rgba(21, 101, 192, 0.8)'}`,
        animation: info.estado === 'llamado' ? 'pulse 2s infinite' : 'none',
      }}
    >
      <Typography variant="h6" fontWeight="bold">Consultorio {id.replace('consultorio_', '')}</Typography>
      <Typography variant="h1" fontWeight="bold" sx={{ my: 1 }}>{info.numero_turno}</Typography>
      <Typography variant="h5" sx={{ mb: 1, wordBreak: 'break-word' }}>{info.nombre_paciente}</Typography>
      
      {/* Servicios en la tarjeta */}
      <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap">
        {info.tipo_servicio?.map((s, idx) => (
          <Chip key={idx} label={s} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontSize: '0.7rem' }} />
        ))}
      </Stack>

      {info.estado === 'llamado' && <SpeakerIcon sx={{ fontSize: 40, mt: 1 }} />}
    </Paper>
  ));

  // === FILAS DE LA COLA ===
  const filasTabla = (data.cola || []).map((p) => (
    <TableRow key={p.numero_turno} sx={{ bgcolor: '#0D1B2A', '&:nth-of-type(odd)': { bgcolor: '#1A3451' } }}>
      <TableCell sx={{ color: 'white', fontSize: '2rem', fontWeight: 'bold', borderBottom: '1px solid #324A6A' }}>
        {p.numero_turno}
      </TableCell>
      <TableCell sx={{ color: '#E0E0E0', fontSize: '1.6rem', borderBottom: '1px solid #324A6A' }}>
        {p.nombre_paciente}
      </TableCell>
      
      {/* NUEVA COLUMNA: SERVICIOS */}
      <TableCell sx={{ borderBottom: '1px solid #324A6A' }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {p.tipo_servicio && p.tipo_servicio.length > 0 ? (
            p.tipo_servicio.map((serv, idx) => (
              <Chip 
                key={idx} 
                label={serv.toUpperCase()} 
                size="small" 
                sx={{ bgcolor: '#1976D2', color: 'white', fontWeight: 'bold', fontSize: '0.9rem' }} 
              />
            ))
          ) : (
            <Typography sx={{ color: '#546E7A', fontStyle: 'italic' }}>Consulta</Typography>
          )}
        </Box>
      </TableCell>

      <TableCell sx={{ color: 'white', fontSize: '1.5rem', borderBottom: '1px solid #324A6A' }}>
        {horaFormateada(p.hora_llegada)}
      </TableCell>
    </TableRow>
  ));

  return (
    <Container maxWidth={false} sx={{ bgcolor: '#0D1B2A', minHeight: '100vh', color: 'white', py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h2" fontWeight="bold" color="#64B5F6">{nombreVeterinaria}</Typography>
          <Typography variant="h4" color="#90CAF9">Pantalla de Cola - {nombreSucursal}</Typography>
        </Box>
        <Box sx={{ width: 120, height: 120, borderRadius: 2, overflow: 'hidden', bgcolor: 'white', p: 1 }}>
          <img src={LogoImage} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 4, mb: 6 }}>
        {tarjetas.length > 0 ? tarjetas : (
          <Paper sx={{ p: 8, bgcolor: '#263238', borderRadius: 4 }}>
            <Typography variant="h4" color="#90CAF9">No hay pacientes en consulta</Typography>
          </Paper>
        )}
      </Box>

      <hr style={{ borderTop: '2px solid #1976D2', margin: '40px 0' }} />

      <Typography variant="h3" fontWeight="bold" align="center" mb={4} color="#BBDEFB">COLA DE ESPERA</Typography>

      <Paper elevation={12} sx={{ borderRadius: 4, overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#1976D2' }}>
              <TableCell sx={{ color: 'white', fontSize: '1.8rem', fontWeight: 'bold' }}>TURNO</TableCell>
              <TableCell sx={{ color: 'white', fontSize: '1.8rem', fontWeight: 'bold' }}>PACIENTE</TableCell>
              <TableCell sx={{ color: 'white', fontSize: '1.8rem', fontWeight: 'bold' }}>SERVICIOS</TableCell>
              <TableCell sx={{ color: 'white', fontSize: '1.8rem', fontWeight: 'bold' }}>HORA</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filasTabla.length > 0 ? filasTabla : (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 8 }}>
                  <Typography variant="h4" color="#90CAF9">La cola está vacía</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Box sx={{ textAlign: 'center', mt: 5 }}>
        <Chip label={wsStatus.toUpperCase()} color={wsStatus === 'conectado' ? 'success' : 'error'} />
      </Box>
    </Container>
  );
};

export default PantallaCola;